/**
 * 영상 제작 (운영자 › 콘텐츠) — 261103-video-production-plan.
 *
 * 결제 기간마다 업체당 2편. 「영상 업로드 계획 체크」를 누르면 업체마다 이번 결제 기간의 영상 수를 세고, 모자란 만큼 주제와
 * 업로드 시각(매일 08:00·20:00, 업체를 통틀어 12시간 간격)을 정해 계획을 만든다. 영상은 PC 의 video 앱(숏폼 FLOW_대량)이
 * 「어드민 계획 가져오기」로 가져가 지금처럼 대본·SNS 검토 뒤 만들고 예약한다.
 * ★ 계획 체크는 버튼으로만 돈다. 여러 번 눌러도 부족분만 만든다. 주제를 만들 때마다 비용이 든다(업체당 한 번).
 * ★ 대상은 기사 작성과 같다 — 구독은 있는데 만들 수 없는 업체는 아래에 이유와 함께 따로 보인다(계획하지 않는다).
 */
import {
  MAX_SEARCH_PHRASES,
  type VideoBoardRow,
  type VideoPlanRow,
  type VideoProfile,
} from '@geo-admin/shared/contract/api/video.ts';
import { video } from '@geo-admin/shared/contract/api/index.ts';
import { Link } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { PageTitle } from '../components/PageTitle.tsx';
import { Button } from '../components/ui/button.tsx';
import { Badge, Card, CardBody, CardHeader } from '../components/ui/card.tsx';
import { Dialog } from '../components/ui/dialog.tsx';
import { FieldError, Input, Label, Textarea } from '../components/ui/form.tsx';
import { ErrorBox, Loading } from '../components/ui/states.tsx';
import { useApi, useApiMutation } from '../lib/api.ts';
import { PATHS } from '../lib/paths.ts';
import { cn } from '../lib/utils.ts';
import { parseVideoCsv } from '../lib/video-csv.ts';

const dot = (d: string) => d.slice(5).replace('-', '.');
const WEEK = ['일', '월', '화', '수', '목', '금', '토'];
/** 업로드 시각(KST) — 11.04(수) 08:00 */
function slotText(iso: string): string {
  const k = new Date(new Date(iso).getTime() + 9 * 3_600_000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(k.getUTCMonth() + 1)}.${p(k.getUTCDate())}(${WEEK[k.getUTCDay()]}) ${p(k.getUTCHours())}:${p(k.getUTCMinutes())}`;
}

const STATUS: Record<VideoPlanRow['status'], { label: string; tone: 'slate' | 'green' | 'amber' | 'red' | 'blue' }> = {
  planned: { label: '계획됨', tone: 'blue' },
  claimed: { label: 'PC가 가져감', tone: 'amber' },
  scheduled: { label: '예약됨', tone: 'green' },
  canceled: { label: '취소', tone: 'slate' },
  failed: { label: '생산 실패', tone: 'red' },
};

export function VideosPage() {
  const got = useApi(video.getVideoBoard);
  const check = useApiMutation(video.checkVideoPlan);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<VideoBoardRow | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  if (got.error) return <ErrorBox error={got.error} />;
  if (!got.data) return <Loading />;
  const b = got.data;
  const short = b.rows.filter((r) => r.need > 0);
  const needsProfile = short.filter((r) => r.profile === null).length;

  const onCheck = async () => {
    setError(null);
    setNotice(null);
    const total = short.reduce((n, r) => n + r.need, 0);
    if (total === 0) {
      setNotice('모든 업체가 이번 결제 기간에 2편을 채웠습니다 — 만들 계획이 없습니다');
      return;
    }
    if (
      !confirm(
        `${short.length}곳 · ${total}편을 계획합니다.\n업체마다 주제를 만드는 데 비용이 조금 듭니다(업체당 한 번).`,
      )
    )
      return;
    try {
      const r = await check.mutateAsync({});
      const parts = [`${r.created}편을 계획했습니다`];
      if (r.gapExceptions > 0) parts.push(`그중 ${r.gapExceptions}편은 칸이 모자라 12시간 간격을 깼습니다`);
      if (r.skipped.length > 0)
        parts.push(`못 한 곳: ${r.skipped.map((s) => `${s.brandName}(${s.reason})`).join(', ')}`);
      setNotice(parts.join(' · '));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <>
      <PageTitle
        title="영상 제작"
        description="결제 기간마다 업체당 2편 — 모자란 만큼 주제와 업로드 시각을 정하고, PC 의 영상 앱이 가져가 만듭니다"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)}>
              정보 가져오기(CSV)
            </Button>
            <Button onClick={() => void onCheck()} disabled={check.isPending || !b.topicWriterReady}>
              {check.isPending ? '계획하는 중… (1분 안팎)' : '영상 업로드 계획 체크'}
            </Button>
          </div>
        }
      />
      {!b.topicWriterReady ? (
        <p className="text-red-700 text-sm">주제 생성 키가 설정되지 않아 계획을 만들 수 없습니다.</p>
      ) : null}
      {notice ? <p className="text-emerald-700 text-sm">{notice}</p> : null}
      <FieldError>{error}</FieldError>

      <Card>
        <CardHeader
          title="업체별 이번 결제 기간"
          description={
            short.length > 0
              ? `부족 ${short.length}곳${needsProfile > 0 ? ` · 그중 ${needsProfile}곳은 정보가 없어 계획할 수 없습니다` : ''}`
              : '모든 업체가 채웠습니다'
          }
        />
        <CardBody>
          <BrandTable rows={b.rows} onEdit={setEditing} />
        </CardBody>
      </Card>

      {b.blocked.length > 0 ? (
        <Card>
          <CardHeader
            title="구독은 있으나 생산 불가"
            description="계획하지 않습니다 — 구독을 정리하면 다음 체크에서 대상이 됩니다"
          />
          <CardBody>
            <ul className="grid gap-1.5 text-sm">
              {b.blocked.map((r) => (
                <li key={r.brandId} className="flex flex-wrap items-baseline gap-2">
                  <Link to={PATHS.admin.brand} params={{ id: r.brandId }} className="font-medium hover:underline">
                    {r.brandName}
                  </Link>
                  <span className="text-slate-500">{r.reason}</span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHeader
          title="계획"
          description="열린 계획 전부와 최근 30일 — 계획됨일 때만 주제를 고치거나 취소할 수 있습니다(PC 가 가져가면 영상 앱에서 다룹니다)"
        />
        <CardBody>
          <PlanTable plans={b.plans} />
        </CardBody>
      </Card>

      <ProfileDialog row={editing} onClose={() => setEditing(null)} />
      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} />
    </>
  );
}

function BrandTable({ rows, onEdit }: { rows: VideoBoardRow[]; onEdit: (r: VideoBoardRow) => void }) {
  if (rows.length === 0) return <p className="text-slate-500 text-sm">생산 가능한 업체가 없습니다.</p>;
  return (
    <table className="w-full text-sm">
      <thead className="border-slate-200 border-b text-left text-slate-400 text-xs">
        <tr>
          <th className="py-2 font-medium">업체</th>
          <th className="py-2 font-medium">결제 기간</th>
          <th className="py-2 text-right font-medium">영상</th>
          <th className="py-2 text-right font-medium">계획</th>
          <th className="py-2 text-right font-medium">부족</th>
          <th className="py-2 pl-4 font-medium">영상 정보</th>
          <th className="py-2" />
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.brandId} className="border-slate-100 border-b hover:bg-slate-50">
            <td className="py-2.5 font-medium">{r.brandName}</td>
            <td className="py-2.5 tabular-nums">
              {dot(r.cycle.from)}~{dot(r.cycle.to)}{' '}
              <span className="text-slate-400 text-xs">
                {r.cycle.daysLeft === 0 ? '오늘 마감' : `D-${r.cycle.daysLeft}`}
              </span>
            </td>
            <td className="py-2.5 text-right tabular-nums">{r.videos}</td>
            <td className="py-2.5 text-right tabular-nums">{r.plans}</td>
            <td className="py-2.5 text-right">
              {r.need > 0 ? <Badge tone="red">{r.need}</Badge> : <span className="text-slate-400">0</span>}
            </td>
            <td className="py-2.5 pl-4">
              {r.profile ? (
                <span className="text-slate-600">
                  {r.profile.category} · {r.profile.searchPhrases.join(', ')}
                  <span className="ml-1 text-slate-400 text-xs">쓴 주제 {r.usedTopics}</span>
                </span>
              ) : (
                <Badge tone="amber">정보 필요</Badge>
              )}
            </td>
            <td className="py-2.5 text-right">
              <Button size="sm" variant="ghost" onClick={() => onEdit(r)}>
                {r.profile ? '편집' : '입력'}
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PlanTable({ plans }: { plans: VideoPlanRow[] }) {
  const update = useApiMutation(video.updateVideoPlan);
  const regenerate = useApiMutation(video.regenerateVideoPlanTopic);
  const cancel = useApiMutation(video.cancelVideoPlan);
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (plans.length === 0) return <p className="text-slate-500 text-sm">아직 계획이 없습니다.</p>;

  const run = async (id: string, job: () => Promise<unknown>) => {
    setError(null);
    setBusy(id);
    try {
      await job();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const now = Date.now();

  return (
    <div className="grid gap-2">
      <FieldError>{error}</FieldError>
      <table className="w-full text-sm">
        <thead className="border-slate-200 border-b text-left text-slate-400 text-xs">
          <tr>
            <th className="w-36 py-2 font-medium">업로드</th>
            <th className="w-40 py-2 font-medium">업체</th>
            <th className="py-2 font-medium">주제</th>
            <th className="w-28 py-2 font-medium">상태</th>
            <th className="w-60 py-2" />
          </tr>
        </thead>
        <tbody>
          {plans.map((p) => {
            const open = p.status === 'planned';
            const late = (p.status === 'planned' || p.status === 'claimed') && new Date(p.slotAt).getTime() < now;
            return (
              <tr
                key={p.id}
                className={cn(
                  'border-slate-100 border-b align-top hover:bg-slate-50',
                  (p.status === 'canceled' || p.status === 'failed') && 'opacity-60',
                )}
              >
                <td className="py-2.5 tabular-nums">
                  {slotText(p.slotAt)}
                  {p.gapException ? (
                    <div>
                      <Badge tone="amber" title="기간 안 빈 칸이 모자라 12시간 간격을 깼습니다">
                        간격 예외
                      </Badge>
                    </div>
                  ) : null}
                  {late ? (
                    <div>
                      <Badge tone="red">시각 지남</Badge>
                    </div>
                  ) : null}
                </td>
                <td className="py-2.5">{p.brandName}</td>
                <td className="py-2.5">
                  {editId === p.id ? (
                    <form
                      className="flex gap-2"
                      onSubmit={(e: FormEvent) => {
                        e.preventDefault();
                        void run(p.id, async () => {
                          await update.mutateAsync({ params: { id: p.id }, body: { topic: draft } });
                          setEditId(null);
                        });
                      }}
                    >
                      <Input value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={120} autoFocus />
                      <Button size="sm" type="submit" disabled={busy === p.id}>
                        저장
                      </Button>
                      <Button size="sm" variant="ghost" type="button" onClick={() => setEditId(null)}>
                        취소
                      </Button>
                    </form>
                  ) : (
                    <>
                      {p.topic}
                      {p.error ? <div className="text-red-700 text-xs">{p.error}</div> : null}
                    </>
                  )}
                </td>
                <td className="py-2.5">
                  <Badge tone={STATUS[p.status].tone}>{STATUS[p.status].label}</Badge>
                </td>
                <td className="py-2.5 text-right">
                  {open && editId !== p.id ? (
                    <div className="flex justify-end gap-1 whitespace-nowrap">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditId(p.id);
                          setDraft(p.topic);
                        }}
                      >
                        수정
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy === p.id}
                        onClick={() => void run(p.id, () => regenerate.mutateAsync({ params: { id: p.id } }))}
                      >
                        {busy === p.id ? '만드는 중…' : '다시 만들기'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy === p.id}
                        onClick={() => {
                          if (!confirm(`${p.brandName} · ${slotText(p.slotAt)} 계획을 취소합니다.`)) return;
                          void run(p.id, () => cancel.mutateAsync({ params: { id: p.id } }));
                        }}
                      >
                        취소
                      </Button>
                    </div>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ProfileDialog({ row, onClose }: { row: VideoBoardRow | null; onClose: () => void }) {
  return (
    <Dialog open={row !== null} onClose={onClose} title={row ? `${row.brandName} · 영상 정보` : ''}>
      {row ? (
        <ProfileForm key={row.brandId} brandId={row.brandId} fallbackName={row.brandName} onDone={onClose} />
      ) : null}
    </Dialog>
  );
}

function ProfileForm({ brandId, fallbackName, onDone }: { brandId: string; fallbackName: string; onDone: () => void }) {
  const got = useApi(video.getVideoProfile, { params: { id: brandId } });
  if (got.error) return <ErrorBox error={got.error} />;
  if (!got.data) return <Loading />;
  return (
    <ProfileFields
      brandId={brandId}
      initial={got.data.profile}
      fallbackName={fallbackName}
      topics={got.data.topics.map((t) => t.topic)}
      onDone={onDone}
    />
  );
}

function ProfileFields({
  brandId,
  initial,
  fallbackName,
  topics,
  onDone,
}: {
  brandId: string;
  initial: VideoProfile | null;
  fallbackName: string;
  topics: string[];
  onDone: () => void;
}) {
  const put = useApiMutation(video.putVideoProfile);
  const [businessName, setBusinessName] = useState(initial?.businessName ?? fallbackName);
  const [category, setCategory] = useState(initial?.category ?? '');
  const [phrases, setPhrases] = useState<string[]>(() => {
    const p = [...(initial?.searchPhrases ?? [])];
    while (p.length < MAX_SEARCH_PHRASES) p.push('');
    return p;
  });
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await put.mutateAsync({
        params: { id: brandId },
        body: { businessName, category, searchPhrases: phrases.map((p) => p.trim()).filter(Boolean) },
      });
      onDone();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <form className="grid gap-3" onSubmit={(e) => void onSubmit(e)}>
      <div className="grid gap-1">
        <Label htmlFor="v-name">업체명(해시태그·SNS 문구에 쓰는 간판명)</Label>
        <Input
          id="v-name"
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          maxLength={60}
          required
        />
      </div>
      <div className="grid gap-1">
        <Label htmlFor="v-cat">카테고리</Label>
        <Input
          id="v-cat"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          placeholder="치과 · 피부과 · 한의원"
          maxLength={40}
          required
        />
      </div>
      <div className="grid gap-1">
        <Label>검색문장(최대 {MAX_SEARCH_PHRASES}개 — 영상 끝 태그로 붙고, 주제 문장에는 쓰지 않습니다)</Label>
        {phrases.map((p, i) => (
          <Input
            // biome-ignore lint/suspicious/noArrayIndexKey: 고정 3칸이다
            key={i}
            value={p}
            onChange={(e) => setPhrases((xs) => xs.map((x, j) => (j === i ? e.target.value : x)))}
            placeholder={['화곡동 치과', '화곡동 치과 추천', '치과 추천'][i]}
            maxLength={80}
          />
        ))}
      </div>
      {topics.length > 0 ? (
        <details className="text-sm">
          <summary className="cursor-pointer text-slate-500">이미 쓴 주제 {topics.length}개</summary>
          <ul className="mt-1 list-disc pl-5 text-slate-600">
            {topics.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        </details>
      ) : null}
      <FieldError>{error}</FieldError>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onDone}>
          닫기
        </Button>
        <Button type="submit" disabled={put.isPending || phrases.every((p) => !p.trim())}>
          저장
        </Button>
      </div>
    </form>
  );
}

function ImportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const imp = useApiMutation(video.importVideoData);
  const [text, setText] = useState('');
  const [files, setFiles] = useState<string[]>([]);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const parsed = parseVideoCsv([...files, ...(text.trim() ? [text] : [])]);

  const onFiles = async (list: FileList | null) => {
    if (!list) return;
    setFiles(await Promise.all([...list].map((f) => f.text())));
  };
  const onSubmit = async () => {
    setError(null);
    setResult(null);
    try {
      const r = await imp.mutateAsync({ body: { items: parsed.items } });
      setResult(
        `업체 정보 ${r.profiles}곳 · 새 주제 이력 ${r.topics}개를 넣었습니다` +
          (r.skipped.length > 0 ? ` · 어드민에 없는 업체 ${r.skipped.length}곳은 건너뛰었습니다` : ''),
      );
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="정보 가져오기(CSV)">
      <div className="grid gap-3 text-sm">
        <p className="text-slate-500">
          숏폼(FLOW_대량)에 올리던 CSV 중 <b>clientId 열이 있는 파일</b>(video/outputs/_csv/*.id.csv)을 올리세요. 여러
          개를 함께 올려도 됩니다. 업체 정보는 덮어쓰고, 주제(주제·주제_260930·이미 사용한 주제)는 이력에 더합니다.
        </p>
        <input type="file" accept=".csv,text/csv" multiple onChange={(e) => void onFiles(e.target.files)} />
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          placeholder="또는 CSV 내용을 붙여넣기"
        />
        <p className="text-slate-600">
          {parsed.rows}줄 → 업체 {parsed.items.length}곳 · 프로필 {parsed.items.filter((i) => i.profile).length}곳 ·
          주제 {parsed.items.reduce((n, i) => n + i.topics.length, 0)}개
        </p>
        {parsed.problems.length > 0 ? (
          <ul className="list-disc pl-5 text-amber-700 text-xs">
            {parsed.problems.slice(0, 8).map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        ) : null}
        {result ? <p className="text-emerald-700">{result}</p> : null}
        <FieldError>{error}</FieldError>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            닫기
          </Button>
          <Button onClick={() => void onSubmit()} disabled={imp.isPending || parsed.items.length === 0}>
            가져오기
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
